<template>
  <div
    class="grey lighten-5 flex-grow-1 d-flex justify-start pl-4 pr-4 pt-3 flex-column "
    :class="$style.root"
  >
    <span class="text-overline pb-2">Händelser</span>
    <div class="d-flex flex-grow-1">
      <EventCreateButton />
      <transition name="slide-x-transition" mode="out-in">
        <EventScrollContainer v-if="Object.keys(sortedEvents).length">
          <EventCard
            v-for="event in sortedEvents"
            :event="event"
            :key="event.uuid"
          />
        </EventScrollContainer>
      </transition>
    </div>

    <div :class="$style.loadingContainer">
      <transition name="slide-x-transition" mode="out-in">
        <v-progress-linear
          :key="$apollo.loading"
          v-if="$apollo.loading"
          height="2"
          indeterminate
          color="primary"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import EventScrollContainer from './EventScrollContainer'
import EventCreateButton from './EventCreateButton'
import EventCard from './EventCard'
import { get } from 'vuex-pathify'
import orderBy from 'lodash.orderby'
const DEFUALT_POOL_INTERVAL = 60000

export default {
  name: 'EventContainer',

  components: {
    EventScrollContainer,
    EventCreateButton,
    EventCard
  },

  apollo: {
    activeEvents: {
      watchLoading(isLoading) {
        this.$store.set('event/fetching', Boolean(isLoading))
      },
      pollInterval: DEFUALT_POOL_INTERVAL,
      query: gql`
        query activeEvents($filter: ActiveEventsInput!) {
          activeEvents(filter: $filter) {
            uuid
            title
            time_start
            district_id
            comment
            basun
            location
            basun_time
            last_sms_message {
              uuid
              event_uuid
              trips {
                trip_uuid
                data {
                  sms_text
                  reason_code
                  bookings {
                    first_name
                    last_name
                    phone_number
                    train_number
                    departure_date
                    time_start
                    time_end
                    place_start
                    place_end
                  }
                }
              }
            }
            sms_messages {
              uuid
              event_uuid
              trips {
                trip_uuid
                data {
                  bookings {
                    first_name
                    last_name
                    phone_number
                    train_number
                    departure_date
                    time_start
                    time_end
                    place_start
                    place_end
                  }
                }
              }
            }
            last_dua_message {
              created
              body {
                response_data
                updated
                trip_uuid
                data
                status
              }
            }
            last_traffic_message {
              created
              event_uuid
              subject
              routes
              delays
              replacements
              yellow_vests
              train_deviations
              turnarounds
              forecast_comment
              replacement_comment
              yellow_vest_comment
              consequence
            }
            routes {
              uuid
              from_place
              to_place
            }
            reason {
              name
              uuid
            }
            operation_level {
              uuid
              name
            }
          }
        }
      `,
      manual: true,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            district_id: this.$store.get('app/selectedDistrictId')
          }
        }
      },
      async result({ data }) {
        if (!data) return
        await this.$store.dispatch('event/mapData', data.activeEvents)

        const statusIsUnknown = await this.$store.dispatch(
          'event/checkUnhandledDuaMessages'
        )

        if (statusIsUnknown) {
          this.$apollo.queries.activeEvents.pollInterval = 10000
        } else {
          this.$apollo.queries.activeEvents.pollInterval = 600000
        }

        const currentEvent = data.activeEvents?.find(
          x => x.uuid === this.selectedEventUUID
        )

        if (this.selectedEventUUID && currentEvent) {
          const currentDuaMessageModel = this.$store.get('duaMessage/model')
          const lastDuaMessage = currentEvent?.last_dua_message || null
          if (
            lastDuaMessage &&
            currentEvent &&
            currentDuaMessageModel &&
            currentDuaMessageModel.body
          ) {
            currentDuaMessageModel?.body?.forEach((body, index) => {
              const message = lastDuaMessage?.body?.find(
                x => x?.trip_uuid === body?.trip_uuid
              )

              const trainDeviations = this.$store.get('trainDeviation/data')

              if (!body.data.cancel_sections) {
                message.data.cancel_sections = [
                  {
                    from: trainDeviations[body.trip_uuid]?.from || null,
                    to: trainDeviations[body.trip_uuid]?.to || null,
                    dua_cancellation_code: 'ANA027',
                    dua_reference_code: 'ANA075',
                    estimated_time_at_location: null,
                    departure: trainDeviations[body.trip_uuid]?.departure
                      ? +new Date(trainDeviations[body.trip_uuid]?.departure)
                      : null,
                    arrival: trainDeviations[body.trip_uuid]?.arrival
                      ? +new Date(trainDeviations[body.trip_uuid]?.arrival)
                      : null,
                    index: 0
                  }
                ]
              } else {
                message.data.cancel_sections = body.data.cancel_sections
              }

              this.$store.set(`duaMessage/model@body[${index}]`, message)
            })
          }
          return
        }
        const event_uuid = this.$route.params.event_uuid
        const currentEventFromParam = data.activeEvents?.find(
          x => x.uuid === event_uuid
        )
        if (event_uuid && currentEventFromParam) {
          this.$store.dispatch('event/selectEvent', event_uuid)
        } else {
          this.$store.dispatch('event/unSelectEvent')
        }
      }
    }
  },

  created() {
    this.$eventBus.$on('event/refetch', this.refetch)
  },

  methods: {
    refetch() {
      this.$apollo.queries.activeEvents.refetch()
    }
  },

  computed: {
    selectedEventUUID: get('event/selectedUUID'),
    selectedEvent: get('event/selectedEvent'),
    events: get('event/data'),

    sortedEvents() {
      return orderBy(this.events, ['time_start'], ['desc'])
    }
  }
}
</script>

<style lang="scss" module>
.root {
  height: 228px;
  position: relative;
}

.loadingContainer {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
}
</style>
